import React from "react";
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { focusRing } from "components/Graphs/utils";
import { RackRive } from "components/landing/rack-rive";
import {
  AddCloudComputeAction,
  BrevConnectActionTrigger,
  BrevConnectAction,
} from "./ComputeCreationActions";

export type ComputeEmptyStateKind =
  | "organization"
  | "cloud"
  | "local"
  | "filtered";

type FocusedComputeEmptyStateKind = Exclude<
  ComputeEmptyStateKind,
  "organization"
>;

interface ComputeEmptyStateProps {
  isAdmin: boolean;
  kind: ComputeEmptyStateKind;
  onAddCloud: () => void;
  selectedComputeLabel: string;
}

const cloudComputeDescription =
  "Create an instance with AWS, Google Cloud, Azure, Lambda, and more, then optionally add Docker containers, MicroK8s, and other tools.";

const localComputeDescription =
  "Connect a DGX Spark, DGX Station, or Ubuntu machine to share with your team.";

const computeOptionRowClassName =
  "group relative cursor-pointer px-4 py-6 text-left transition-colors duration-200 hover:bg-white/[0.025] focus-within:bg-white/[0.025]";

const optionOverlayButtonClassName = `absolute inset-0 z-10 cursor-pointer border-0 bg-transparent p-0 ${focusRing.join(" ")}`;

const actionClassName =
  "inline-flex items-center justify-start gap-1.5 text-left text-sm font-medium text-white";

const rowTitleLabelClassName =
  "relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-highlight after:content-[''] after:transition-transform after:duration-300 after:ease-out group-hover:after:scale-x-100 group-focus-within:after:scale-x-100 motion-reduce:after:transition-none";

const actionArrowClassName =
  "h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-focus-within:translate-x-1 motion-reduce:transform-none";

const optionTitleClassName =
  "inline-flex w-fit items-center gap-1.5 text-sm font-semibold tracking-tight text-white";

const AnimatedActionLabel: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <>
    <span className={rowTitleLabelClassName}>{children}</span>
    <ArrowRightIcon aria-hidden="true" className={actionArrowClassName} />
  </>
);

interface ComputeOptionRowProps {
  actionLabel: string;
  children?: React.ReactNode;
  description: string;
  isVisualAnimated?: boolean;
  onClick?: () => void;
  renderTrigger?: (trigger: React.ReactElement) => React.ReactNode;
  title: string;
  visual: React.ReactNode;
}

const ComputeOptionRow: React.FC<ComputeOptionRowProps> = ({
  actionLabel,
  children,
  description,
  isVisualAnimated = false,
  onClick,
  renderTrigger,
  title,
  visual,
}) => {
  const trigger = (
    <button
      aria-label={actionLabel}
      className={optionOverlayButtonClassName}
      onClick={onClick}
      type="button"
    />
  );

  return (
    <article className={computeOptionRowClassName}>
      {renderTrigger ? renderTrigger(trigger) : trigger}
      <div className="pointer-events-none flex min-w-0 items-center gap-5">
        <div
          aria-hidden="true"
          className={`flex h-24 w-24 shrink-0 items-center justify-center opacity-80 ${
            isVisualAnimated ? "overflow-hidden motion-reduce:hidden" : ""
          }`}
        >
          {visual}
        </div>
        <div className="min-w-0 max-w-3xl">
          <h3 className={optionTitleClassName}>
            <AnimatedActionLabel>{title}</AnimatedActionLabel>
          </h3>
          <p className="mt-2 text-sm leading-6 text-secondary">{description}</p>
          {children}
        </div>
      </div>
    </article>
  );
};

const renderRegisterComputeTrigger = (trigger: React.ReactElement) => (
  <BrevConnectActionTrigger>{trigger}</BrevConnectActionTrigger>
);

const OrganizationComputeEmptyState: React.FC<{
  isAdmin: boolean;
  onAddCloud: () => void;
}> = ({ isAdmin, onAddCloud }) => {
  if (!isAdmin) {
    return (
      <section aria-label="Compute setup options" className="py-6">
        <div className="flex min-h-[280px] items-center justify-center py-8 lg:py-10">
          <div className="flex w-full max-w-3xl flex-col items-center justify-center gap-6 sm:flex-row sm:gap-10">
            <div className="max-w-sm text-left">
              <h2 className="text-base font-semibold tracking-tight text-white">
                No compute resources
              </h2>
              <p className="mt-3 text-sm leading-6 text-secondary">
                {cloudComputeDescription}
              </p>
              <button
                className={`group mt-6 rounded-sm ${actionClassName} ${focusRing.join(" ")}`}
                onClick={onAddCloud}
                type="button"
              >
                <AnimatedActionLabel>
                  Create a cloud instance
                </AnimatedActionLabel>
              </button>
            </div>
            <div
              aria-hidden="true"
              className="h-52 w-24 shrink-0 overflow-hidden opacity-80 motion-reduce:hidden"
            >
              <RackRive className="h-full w-full" scale={0.9} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Compute setup options"
      className="mx-auto max-w-2xl py-6"
    >
      <div className="mb-5 px-4 text-center">
        <h2 className="text-base font-semibold tracking-tight text-white">
          No compute resources
        </h2>
        <p className="mt-1 text-sm leading-6 text-secondary">
          How would you like to add compute resources to your organization?
        </p>
      </div>
      <div className="divide-y divide-white/[0.08] border-y border-white/[0.08]">
        <ComputeOptionRow
          actionLabel="Create a cloud instance"
          description={cloudComputeDescription}
          isVisualAnimated
          onClick={onAddCloud}
          title="Create a cloud instance"
          visual={<RackRive className="h-full w-full" scale={0.9} />}
        />

        <ComputeOptionRow
          actionLabel="Connect a local device"
          description={localComputeDescription}
          renderTrigger={renderRegisterComputeTrigger}
          title="Add your own hardware with Brev Connect"
          visual={
            <img alt="" className="h-auto w-full" src="/assets/spark.svg" />
          }
        >
          <a
            className={`relative z-20 mt-2 inline-flex pointer-events-auto items-center gap-1.5 text-sm text-white transition-colors duration-200 hover:text-highlight ${focusRing.join(" ")}`}
            href="https://docs.nvidia.com/brev/concepts/brev-connect"
            rel="noreferrer"
            target="_blank"
          >
            Brev Connect docs
            <ArrowTopRightOnSquareIcon
              aria-hidden="true"
              className="h-3.5 w-3.5 shrink-0"
            />
          </a>
        </ComputeOptionRow>
      </div>
    </section>
  );
};

const emptyStateContent = (
  kind: FocusedComputeEmptyStateKind,
  selectedComputeLabel: string,
  isAdmin: boolean,
) => {
  switch (kind) {
    case "local":
      if (!isAdmin) {
        return {
          title:
            "No devices have been added to your organization via Brev Connect",
          description:
            "You don't have access to add devices to this organization. Ask an organization admin to register hardware.",
        };
      }
      return {
        title: "Add your own device with Brev Connect",
        description: localComputeDescription,
      };
    case "cloud":
      return {
        title: "Create a cloud instance",
        description:
          "Launch an instance from a range of cloud providers to share with your team.",
      };
    case "filtered":
      return {
        title: `No ${selectedComputeLabel} matches your filters`,
        description: "Try adjusting or clearing your filters.",
      };
  }
};

const EmptyStateActions: React.FC<{
  isAdmin: boolean;
  kind: FocusedComputeEmptyStateKind;
  onAddCloud: () => void;
}> = ({ isAdmin, kind, onAddCloud }) => {
  switch (kind) {
    case "local":
      return <BrevConnectAction isAdmin={isAdmin} />;
    case "cloud":
      return <AddCloudComputeAction onAddCloud={onAddCloud} />;
    case "filtered":
      return null;
  }
};

const ComputeEmptyState: React.FC<ComputeEmptyStateProps> = ({
  isAdmin,
  kind,
  onAddCloud,
  selectedComputeLabel,
}) => {
  if (kind === "organization") {
    return (
      <OrganizationComputeEmptyState
        isAdmin={isAdmin}
        onAddCloud={onAddCloud}
      />
    );
  }

  const content = emptyStateContent(kind, selectedComputeLabel, isAdmin);
  const [titleBeforeBrevConnect, titleAfterBrevConnect] =
    content.title.split("Brev Connect");
  const title =
    isAdmin && content.title.includes("Brev Connect") ? (
      <>
        {titleBeforeBrevConnect}
        <span className="bg-gradient-to-r from-highlight to-highlightDarker bg-clip-text text-transparent">
          Brev Connect
        </span>
        {titleAfterBrevConnect}
      </>
    ) : (
      content.title
    );

  return (
    <section
      aria-label={content.title}
      className="rounded-xl py-16 text-center"
    >
      <div className="flex flex-1 flex-col items-center justify-center">
        <h3 className="text-base font-semibold text-secondary">{title}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-zinc-500">
          {content.description}
        </p>
        {kind !== "filtered" ? (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <EmptyStateActions
              isAdmin={isAdmin}
              kind={kind}
              onAddCloud={onAddCloud}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ComputeEmptyState;
